'use client';

import { useMemo } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { isSuperAdmin } from '@/features/auth';

/**
 * React-side permission checks.
 *
 * Lookups are O(1): the user's `permissions` and `roles` arrays are wrapped in
 * `Set`s once per `user` change, so the result is a stable callback bag that
 * runs in constant time per check — matters in hot paths like
 * `useFilteredColumns` over hundreds of dynamic columns.
 */
export function usePermissions() {
  const { user } = useAuth();

  return useMemo(() => {
    const superAdmin = isSuperAdmin(user);
    const permSet = user ? new Set(user.permissions) : null;
    const roleSet = user ? new Set(user.roles) : null;

    const hasPermission = (permission: string): boolean => {
      if (!user) return false;
      if (superAdmin) return true;
      return permSet!.has(permission);
    };

    const hasRole = (role: string): boolean => {
      if (!user) return false;
      if (superAdmin) return true;
      return roleSet!.has(role);
    };

    return {
      isSuperAdmin: superAdmin,
      hasRole,
      hasAnyRole: (roles: string[]) => roles.some(hasRole),
      hasPermission,
      hasAnyPermission: (permissions: string[]) =>
        permissions.some(hasPermission),
      hasAllPermissions: (permissions: string[]) =>
        permissions.every(hasPermission)
    };
  }, [user]);
}
